/**
 * Encounter DRG – design tokens
 *
 * Colour values are lifted directly from the Tailwind palette used in the
 * reference scratchpad so the two UIs stay pixel-consistent.
 * Keep every visual constant here so tweaks happen in one place.
 */

import type { CSSProperties } from "react";

/* ─── Tailwind colour aliases ─── */

export const TW = {
  // Gray
  gray50: "#f9fafb",
  gray100: "#f3f4f6",
  gray200: "#e5e7eb",
  gray400: "#9ca3af",
  gray500: "#6b7280",
  gray600: "#4b5563",
  gray700: "#374151",
  gray800: "#1f2937",
  gray900: "#111827",

  // Fuchsia (CDI)
  fuchsia50: "#fdf4ff",
  fuchsia100: "#fae8ff",
  fuchsia200: "#f5d0fe",
  fuchsia500: "#d946ef",
  fuchsia700: "#a21caf",
  fuchsia900: "#701a75",

  // Yellow (CODING)
  yellow50: "#fefce8",
  yellow100: "#fef9c3",
  yellow200: "#fef08a",
  yellow500: "#eab308",
  yellow700: "#a16207",
  yellow800: "#854d0e",
  yellow900: "#713f12",

  // Green
  green50: "#f0fdf4",
  green100: "#dcfce7",
  green200: "#bbf7d0",
  green300: "#86efac",
  green500: "#22c55e",
  green600: "#16a34a",
  green700: "#15803d",
  green800: "#166534",

  // Red
  red50: "#fef2f2",
  red100: "#fee2e2",
  red200: "#fecaca",
  red300: "#fca5a5",
  red500: "#ef4444",
  red600: "#dc2626",
  red700: "#b91c1c",
  red800: "#991b1b",

  // Orange
  orange50: "#fff7ed",
  orange100: "#ffedd5",
  orange200: "#fed7aa",
  orange300: "#fdba74",
  orange500: "#f97316",
  orange600: "#ea580c",
  orange700: "#c2410c",
  orange800: "#9a3412",

  // Blue
  blue100: "#dbeafe",
  blue300: "#93c5fd",
  blue700: "#1d4ed8",
  blue800: "#1e40af",

  // Purple (SME)
  purple50: "#faf5ff",
  purple200: "#e9d5ff",
  purple700: "#7e22ce",

  black: "#000000",
  white: "#ffffff",
} as const;

/* ─── Font sizes (matching Tailwind text-xs / text-sm / text-xl) ─── */

export const FONT = {
  xs: 12,
  sm: 14,
  md: 16,
  lg: 18,
  xl: 20,
} as const;

/* ─── Spacing / radius tokens (Tailwind → px) ─── */

export const TIMELINE_SPACING = {
  /** gap-3 between dot column and content */
  gap: 12,
  /** w-3 h-3 dot size */
  dotSize: 12,
  /** w-0.5 line width */
  lineWidth: 2,
  /** h-1 top spacer above dot */
  spacerHeight: 4,
  /** pb-4 bottom padding per item */
  itemPaddingBottom: 16,
  /** rounded-lg card border-radius */
  cardRadius: 8,
  /** px-2 card horizontal padding */
  cardPx: 8,
  /** py-1.5 card vertical padding */
  cardPy: 6,
  /** rounded inner-box border-radius (comment, diff, DRG info) */
  innerRadius: 4,
  /** p-2 inner-box padding */
  innerPadding: 8,
  /** mt-2 inner-box margin-top */
  innerMarginTop: 8,
  /** mt-1 small margin (version label, codes count, DRG value) */
  smallMarginTop: 4,
  /** space-y-1 gap inside revision diff stack */
  diffGap: 4,
} as const;

/* ─── Timeline dot styles per variant ─── */

export type TimelineVariant =
  | "neutral"
  | "cdi"
  | "coding"
  | "success"
  | "danger"
  | "warning";

const DOT_BASE: CSSProperties = {
  width: 12,
  height: 12,
  borderRadius: "50%",
  flexShrink: 0,
  borderWidth: 1,
  borderStyle: "solid",
};

export const TIMELINE_DOT: Record<TimelineVariant, CSSProperties> = {
  neutral: { ...DOT_BASE, background: TW.gray100, borderColor: TW.gray400 },
  cdi: { ...DOT_BASE, background: TW.fuchsia100, borderColor: TW.fuchsia500 },
  coding: { ...DOT_BASE, background: TW.yellow100, borderColor: TW.yellow500 },
  success: { ...DOT_BASE, background: TW.gray100, borderColor: TW.gray400 },
  danger: { ...DOT_BASE, background: TW.gray100, borderColor: TW.gray400 },
  warning: { ...DOT_BASE, background: TW.gray100, borderColor: TW.gray400 },
};

/* ─── Timeline card (section container) styles per variant ─── */

export type TimelineCardTheme = { bg: string; border: string };

export const TIMELINE_CARD: Record<TimelineVariant, TimelineCardTheme> = {
  neutral: { bg: TW.gray50, border: TW.gray200 },
  cdi: { bg: TW.fuchsia50, border: TW.fuchsia200 },
  coding: { bg: TW.yellow50, border: TW.yellow200 },
  success: { bg: TW.green50, border: TW.green200 },
  danger: { bg: TW.red50, border: TW.red200 },
  warning: { bg: TW.purple50, border: TW.purple200 },
};

/* ─── Timeline title colour per variant ─── */

export const TIMELINE_TITLE_COLOR: Record<TimelineVariant, string> = {
  neutral: TW.gray900,
  cdi: TW.gray900,
  coding: TW.black,
  success: TW.green700,
  danger: TW.red700,
  warning: TW.purple700,
};

/* ─── Common inner-box styles (comment / revision-diff boxes) ─── */

export const INNER_BOX = {
  bg: TW.gray50,
  border: TW.gray200,
  labelColor: TW.black,
  textColor: TW.black,
} as const;

/**
 * Per-variant inner-box overrides.
 * The scratchpad gives success/danger their own bg + text colours for the
 * message box; all other variants fall back to INNER_BOX defaults.
 */
export type InnerBoxTheme = {
  bg: string;
  border: string;
  labelColor: string;
  textColor: string;
};

export const INNER_BOX_VARIANT: Record<TimelineVariant, InnerBoxTheme> = {
  neutral: { ...INNER_BOX },
  cdi: { ...INNER_BOX },
  coding: { ...INNER_BOX },
  success: {
    bg: TW.green50,
    border: TW.green200,
    labelColor: TW.green800,
    textColor: TW.green700,
  },
  danger: {
    bg: TW.red50,
    border: TW.red200,
    labelColor: TW.red800,
    textColor: TW.red700,
  },
  warning: {
    bg: TW.purple50,
    border: TW.purple200,
    labelColor: TW.purple700,
    textColor: TW.purple700,
  },
};

/* ─── Per-variant DRG info box overrides ─── */

export type DrgInfoBoxTheme = {
  bg: string;
  border: string;
};

export const DRG_INFO_VARIANT: Record<TimelineVariant, DrgInfoBoxTheme> = {
  neutral: { bg: TW.gray50, border: TW.gray200 },
  cdi: { bg: TW.gray50, border: TW.gray200 },
  coding: { bg: TW.gray50, border: TW.gray200 },
  success: { bg: TW.green50, border: TW.green200 },
  danger: { bg: TW.red50, border: TW.red200 },
  warning: { bg: TW.purple50, border: TW.purple200 },
};

/* ─── Timeline connecting line ─── */

export const TIMELINE_LINE = {
  width: 2,
  color: TW.gray200,
} as const;

/* ─── DRG Comparison outer border + bg ─── */

export const DRG_COMPARISON = {
  matchBorder: TW.green500,
  matchBg: TW.green50,
  positiveImpactBorder: TW.green500,
  positiveImpactBg: TW.white,
  negativeImpactBorder: TW.orange500,
  negativeImpactBg: TW.orange50,
  mismatchBorder: TW.red500,
  mismatchBg: TW.red50,
} as const;

/* ─── CDI / CODING inner cards in DRG comparison ─── */

export const CDI_CARD = {
  bg: TW.fuchsia100,
  border: TW.fuchsia200,
  labelColor: TW.fuchsia700,
  valueColor: TW.fuchsia900,
} as const;

export const CODING_CARD = {
  bg: TW.yellow100,
  border: TW.yellow200,
  labelColor: TW.yellow700,
  valueColor: TW.yellow900,
} as const;

/* ─── Badge type colours ─── */

export const TYPE_BADGE = {
  MED: { bg: TW.blue100, text: TW.blue800, border: TW.blue300 },
  SURG: { bg: TW.red100, text: TW.red800, border: TW.red300 },
} as const;

/* ─── DRG Info box (inside timeline) ─── */

export const DRG_INFO_BOX = {
  bg: TW.gray50,
  border: TW.gray200,
  labelColor: TW.gray700,
  dollarBg: TW.green100,
  dollarText: TW.green800,
  dollarBorder: TW.green300,
} as const;

/* ─── Arrow colour helpers ─── */

export const ARROW_COLOR = {
  match: TW.green600,
  positive: TW.green600,
  negative: TW.orange600,
  mismatch: TW.red600,
} as const;

/* ─── Collapsible section colours ─── */

export const SECTION = {
  bg: TW.gray50,
  border: TW.gray200,
  confirmedTitle: TW.green700,
  removedTitle: TW.red700,
  queryTitle: TW.orange600,
  queryBadgeBg: TW.orange100,
  queryBadgeText: TW.orange800,
  queryBadgeBorder: TW.orange300,
} as const;

/* ─── Revision diff text colours ─── */

export const DIFF = {
  addedColor: TW.green700,
  removedColor: TW.red700,
} as const;

/* ─── Net impact colours ─── */

export const IMPACT = {
  positive: TW.green700,
  negative: TW.orange700,
} as const;
